<template>
    <form @submit.prevent="canEdit ? onSubmit() : null">
        <div class="card mb-5">
            <div class="bg-dark card-header text-white">
                <div class="align-items-end row">
                    <div class="col-8">
                        {{address.label}}
                    </div>
                    <div class="col-4 text-right" v-if="canEdit">
                        <button @click="isEditing=true" class="btn btn-edit-address btn-sm btn-warning d-print-none"
                                type="button" v-show="!isEditing">
                            <i class="fa fa-pencil-alt"></i> Edit
                        </button>
                    </div>
                </div>
            </div>

            <div class="card-body">
                <div class="row">
                    <div class="col-sm-12 form-group">
                        <label for="Address1">Address Line 1</label>
                        <input class="form-control" disabled placeholder="Address Line 1"
                               v-model="address.address1"/>
                    </div>
                    <div class=" col-sm-12 form-group">
                        <label for="Address2">Address Line 2 / Building</label>
                        <select :class="{'is-invalid': isEditing && $v.address.buildingId.$invalid}"
                                :disabled="!isEditing || !canEdit"
                                class="form-control"
                                v-model="address.buildingId">
                            <option :value="null" disabled>-- Select Building --</option>
                            <option :key="building.id" :value="building.id" v-for="building in buildingList">
                                {{building.name}}
                            </option>
                        </select>
                        <span class="invalid-feedback" v-if="isEditing && !$v.address.buildingId.required">Field is required</span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-4 col-sm-6 form-group">
                        <label for="City">City </label>
                        <input :value="selectedBuilding.city" class="form-control" disabled
                               placeholder="City"/>
                    </div>
                    <div class="col-lg-4 col-sm-6 form-group">
                        <label for="State">State </label>
                        <input :value="selectedBuilding.stateCode" class="form-control" disabled
                               placeholder="State"/>
                    </div>
                    <div class="col-lg-4 col-sm-6 form-group">
                        <label asp-for="ZipCode">Zip Code</label>
                        <input :value="selectedBuilding.zipCode" class="form-control" disabled
                               placeholder="Zip Code"/>
                    </div>
                    <div class="col-lg-4 col-sm-6 form-group">
                        <label for="MailStop">Mail Stop</label>
                        <input :value="selectedBuilding.mailStop" class="form-control" disabled
                               placeholder="Mail Stop"/>
                    </div>
                    <div class="col-lg-4 col-sm-6 form-group">
                        <label for="Suite">Suite</label>
                        <input :disabled="!isEditing || !canEdit" autocomplete="off" class="form-control"
                               maxlength="30" placeholder="Suite" v-model="address.suite"/>
                    </div>
                    <div class="col-lg-4 col-sm-6 form-group">
                        <label for="RoomNumber">Room Number</label>
                        <input :disabled="!isEditing || !canEdit" autocomplete="off" class="form-control"
                               maxlength="30" placeholder="Room Number" v-model="address.roomNumber"/>
                    </div>
                </div>
            </div>

            <div class="card-footer d-print-none text-right" style="display: none" v-show="isEditing">
                <button :disabled="isSubmitting" class="btn btn-primary btn-save-address" type="submit">Save</button>
            </div>

        </div>
    </form>
</template>

<script>
    import AddressService from "@/services/AddressService";
    import {Address} from "@/services/models/Address";
    import {required} from 'vuelidate/lib/validators'
    import {ToastType} from "@/services/models/shared/ToastType";
    import {ApplicationRole} from "@/services/models/Role";

    export default {
        data() {
            return {
                isEditing: false,
                isSubmitting: false,
            }
        },
        props: {
            personId:
                {
                    type: String,
                    required: true
                },
            address: {
                type: Address,
                required: true
            },
            buildingList: {
                type: Array,
                required: true
            }
        },
        computed: {
            selectedBuilding() {
                if (this.address.buildingId === null)
                    return {};

                const building = this.buildingList.find(b => b.id === this.address.buildingId);

                if (building)
                    return building;

                return {};
            },
            /**
             * @description Indicates if this form can be edited by the person based on the given role
             * @returns {boolean}
             */
            canEdit() {
                return this.hasApplicationRole(ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities);
            }
        },
        methods: {
            onSubmit() {
                if (!this.canEdit)
                    return;

                const vm = this;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;
                // If this address id exists, then it is an update
                if (this.address.id) {
                    AddressService.updatePersonAddress(vm.address)
                        .then(() => {
                            vm.alert(ToastType.Success, "The address was successfully updated")
                        }).catch((error) => {
                        vm.alert(ToastType.Danger, error.response.statusText)
                    }).finally(() => {
                        vm.isEditing = false;
                        vm.isSubmitting = false;
                    });
                } else {
                    AddressService.createPersonAddress({
                        personId: vm.personId,
                        ...vm.address
                    })
                        .then((response) => {
                            vm.address.id = response.data;
                            vm.alert(ToastType.Success, "The address was successfully updated")
                        }).catch((error) => {
                        vm.alert(ToastType.Danger, error.response.statusText)
                    }).finally(() => {
                        vm.isEditing = false;
                        vm.isSubmitting = false;
                    });
                }
            },
        },
        validations: {
            address: {
                buildingId: {
                    required
                }
            }
        }
    }
</script>
